import './general.course.page.css';
import CourseAbout from './about-the-course/course-about';
import TeachingStaff, {
  TeachingStaffDatamodel,
} from './teaching-staff/teaching-staff';
import TestAndExam from './testAndExam/testAndExam';
import { useCallback, useEffect, useMemo, useState } from 'react';
import globalCourseStore from '../../../stores/global-course-store';
import { transformToStaffOperation } from './operation/data-transfer/transform-to-staff-operation';
import dateFormat from '../../../utils/date-format';
import { observer } from 'mobx-react-lite';

export interface GeneralCoursePageDatamodel {
  isProfessor: boolean;
}

const GeneralCoursePage: React.FC<GeneralCoursePageDatamodel> = observer(
  ({ isProfessor }) => {
    const [staff, setStaff] = useState<TeachingStaffDatamodel[]>([]);
    // Update lecturers and drill down through props
    useEffect(() => {
      if (globalCourseStore.getCurrentCourse()?.metadata) {
        const courseDetails = globalCourseStore.getCurrentCourse()!.details;
        const staffData = transformToStaffOperation(
          courseDetails?.lecturer ?? [],
        );
        setStaff(staffData);
      }
    }, [
      globalCourseStore.currentId,
      globalCourseStore.getCurrentCourse()?.details?.lecturer,
    ]);

    const assignmentAndExamDataModel = useMemo(() => {
      const currentCourse = globalCourseStore.getCurrentCourse();
      if (currentCourse) {
        const assignmnets = currentCourse?.details?.assignments.map(
          ({ assignmentName, dueDate, assignmentWeight }) => ({
            assignmentName,
            dueDate: dueDate,
            assignmentWeight,
            isCompletionGrade: false,
          }),
        );

        return assignmnets;
      }
      return [];
    }, [
      globalCourseStore.currentId,
      globalCourseStore.getCurrentCourse()?.details?.assignments,
    ]);

    const examDataModel = useMemo(() => {
      const currentCourse = globalCourseStore.getCurrentCourse();

      if (currentCourse) {
        const assignmentsPoints = currentCourse?.details.assignments.reduce(
          (sum, assignmentItem) => sum + assignmentItem.assignmentWeight,
          0,
        );

        const total = currentCourse?.details.exams
          .filter((item) => !item.completionGrade)
          .reduce((sum, exam) => sum + exam.points, 0);

        const assignmnets = currentCourse?.details?.exams.map((item) => ({
          assignmentName: item.examName,
          dueDate: '',
          assignmentWeight: item.completionGrade
            ? 100 - total - assignmentsPoints
            : item.points,
          isCompletionGrade: item.completionGrade,
        }));

        return assignmnets;
      }
      return [];
    }, [
      globalCourseStore.currentId,
      globalCourseStore.getCurrentCourse()?.details?.assignments,
      globalCourseStore.getCurrentCourse()?.details?.exams,
    ]);
    return (
      <div>
        <div
          style={{ alignItems: 'stretch' }}
          className={'general-course-page-card-row'}
        >
          <div className={'general-course-page-card-column'}>
            <CourseAbout isProfessor={isProfessor} />

            <div style={{ height: 35 }} />

            <TeachingStaff staff={staff} isProfessor={isProfessor} />
          </div>

          <div className={'general-course-page-space-width'}></div>

          <div style={{ flexGrow: 1 }}>
            <TestAndExam
              data={{
                assignments: assignmentAndExamDataModel ?? [],
                exams: examDataModel ?? [],
              }}
            />
          </div>
        </div>
      </div>
    );
  },
);

const useStyles = () => {
  return useCallback(
    () => ({
      root: {
        position: 'relative',
        width: '200px',
        height: '200px',
        fontFamily: 'Arial, sans-serif',
      },
      svg: {
        transform: 'rotate(-90deg)',
      },
      circle: {
        fill: 'none',
        stroke: '#e0e0e0',
        strokeWidth: '10',
      },
      progress: {
        fill: 'none',
        strokeWidth: '10',
        strokeLinecap: 'round',
        transition: 'stroke-dashoffset 0.5s ease',
      },
      text: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
      },
      score: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        margin: 0,
      },
      label: {
        fontSize: '0.9rem',
        margin: 0,
      },
      legend: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '10px',
      },
      legendItem: {
        display: 'flex',
        alignItems: 'center',
        marginRight: '10px',
      },
      legendColor: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        marginRight: '5px',
      },
      legendText: {
        fontSize: '0.8rem',
      },
    }),
    [],
  );
};

const CircularProgressChart = () => {
  const styles = useStyles();

  const radius = 90;
  const circumference = 2 * Math.PI * radius;

  const data = [
    { label: 'מלגות', percentage: 40, color: '#4CAF50' },
    { label: 'מבחנים', percentage: 40, color: '#3F51B5' },
    { label: 'נוכחות', percentage: 20, color: '#FFC107' },
  ];

  let accumulatedPercentage = 0;

  return (
    <div
      style={{
        position: 'relative',
        width: '200px',
        height: '200px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <svg width="200" height="200" style={styles().svg}>
        <circle cx="100" cy="100" r={radius} style={styles().circle} />
        {data.map((item, index) => {
          const offset =
            circumference - (item.percentage / 100) * circumference;
          const rotation = (accumulatedPercentage / 100) * 360;
          accumulatedPercentage += item.percentage;

          return (
            <circle
              key={index}
              cx="100"
              cy="100"
              r={radius}
              style={{
                fill: 'none',
                strokeWidth: '10',
                strokeLinecap: 'round',
                transition: 'stroke-dashoffset 0.5s ease',
                stroke: item.color,
                strokeDasharray: circumference,
                strokeDashoffset: offset,
                transform: `rotate(${rotation}deg)`,
                transformOrigin: '100px 100px',
              }}
            />
          );
        })}
      </svg>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <p style={styles().score}>100</p>
        <p style={styles().label}>מבחנה הציון</p>
      </div>
      <div style={styles().legend}>
        {data.map((item, index) => (
          <div key={index} style={styles().legendItem}>
            <div
              style={{ ...styles().legendColor, backgroundColor: item.color }}
            ></div>
            <span
              style={styles().legendText}
            >{`${item.label} ${item.percentage}%`}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeneralCoursePage;
